import type { ReactElement } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatMinor } from '~/api/client'
import type { AllocationCategory, AllocationRow } from './allocation'
import { compactMinor, formatAxisDate, formatShareBp } from './format'
import { daysBetween } from '~/state/dates'
import { seriesColor } from './theme'

export type AllocationMode = 'share' | 'value'

export function AllocationChart({
  categories,
  rows,
  mode,
  baseCurrency,
}: {
  categories: readonly AllocationCategory[]
  rows: readonly AllocationRow[]
  mode: AllocationMode
  baseCurrency: string
}): ReactElement {
  const first = rows[0]
  const last = rows[rows.length - 1]
  const spanDays = first && last ? daysBetween(first.date, last.date) : 0

  const data = rows.map((row) => ({
    date: row.date,
    totalMinor: row.totalMinor,
    ...Object.fromEntries(
      categories.map((category) => [category.key, row.valueMinor[category.key] ?? 0]),
    ),
  }))

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{ top: 8, right: 34, bottom: 0, left: 8 }}
        stackOffset={mode === 'share' ? 'expand' : 'none'}
      >
        <CartesianGrid stroke="var(--nw-grid)" strokeWidth={1} vertical={false} />
        <XAxis
          dataKey="date"
          axisLine={{ stroke: 'var(--nw-axis)' }}
          tickLine={false}
          minTickGap={48}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          tickFormatter={(date: string) => formatAxisDate(date, spanDays)}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          width={62}
          tick={{ fill: 'var(--nw-muted)', fontSize: 12 }}
          domain={mode === 'share' ? [0, 1] : undefined}
          tickFormatter={(value: number) =>
            mode === 'share' ? formatShareBp(Math.round(value * 10_000)) : compactMinor(value)
          }
        />

        <Tooltip
          cursor={{ stroke: 'var(--nw-axis)', strokeWidth: 1 }}
          content={({ active, payload, label }) =>
            active ? (
              <AllocationTooltip
                date={String(label)}
                categories={categories}
                row={payload?.[0]?.payload as { totalMinor?: number } & Record<string, number>}
                mode={mode}
                baseCurrency={baseCurrency}
              />
            ) : null
          }
        />

        {categories.map((category) => (
          <Area
            key={category.key}
            type="monotone"
            dataKey={category.key}
            name={category.label}
            stackId="allocation"
            stroke="var(--nw-surface)"
            strokeWidth={2}
            fill={seriesColor(category.slot)}
            fillOpacity={0.85}
            isAnimationActive={false}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  )
}

function AllocationTooltip({
  date,
  categories,
  row,
  mode,
  baseCurrency,
}: {
  date: string
  categories: readonly AllocationCategory[]
  row: ({ totalMinor?: number } & Record<string, number>) | undefined
  mode: AllocationMode
  baseCurrency: string
}): ReactElement | null {
  if (!row) return null
  const total = row.totalMinor ?? 0

  return (
    <div className="nw-tooltip">
      <h3>{date}</h3>
      <dl>
        {categories.map((category) => {
          const valueMinor = row[category.key] ?? 0
          return (
            <div key={category.key} style={{ display: 'contents' }}>
              <dt>
                <span className="nw-key" style={{ background: seriesColor(category.slot) }} />
              </dt>
              <dd className="nw-tooltip-name">{category.label}</dd>
              <dd className="nw-tooltip-value">
                {mode === 'share' && total > 0
                  ? formatShareBp(Math.round((valueMinor * 10_000) / total))
                  : formatMinor(valueMinor, baseCurrency)}
              </dd>
            </div>
          )
        })}
      </dl>
    </div>
  )
}
